import React, { useState } from 'react'
import { updateApplicationApplicant } from '@/api/tapApplications'
import { TapApplication } from '@/types/index'

// Bug #167 — inline-editable applicant block. Recruiters can patch
// first/last name, email, phone, location, and LinkedIn URL without leaving
// the Application detail page. Only the applicant fields ship through the
// dedicated `/applicant` PATCH endpoint so the application-level PATCH stays
// focused on position / stage / flag / assignee state.
interface ApplicantEditorProps {
  application: TapApplication
  onUpdated: (app: TapApplication) => void
}

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const fieldRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginBottom: '16px',
}

const ApplicantEditor: React.FC<ApplicantEditorProps> = ({ application, onUpdated }) => {
  const [editing, setEditing] = useState(false)
  const [firstName, setFirstName] = useState(application.applicant?.firstName || '')
  const [lastName, setLastName] = useState(application.applicant?.lastName || '')
  const [email, setEmail] = useState(application.applicant?.email || '')
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setFirstName(application.applicant?.firstName || '')
    setLastName(application.applicant?.lastName || '')
    setEmail(application.applicant?.email || '')
  }

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      alert('First name, last name, and email are required.')
      return
    }
    setSaving(true)
    try {
      const updated = await updateApplicationApplicant(application.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
      })
      onUpdated(updated)
      setEditing(false)
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(detail || 'Failed to update applicant')
    } finally {
      setSaving(false)
    }
  }

  const applicant = application.applicant

  if (!editing) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
            Applicant
          </h3>
          {applicant && (
            <button
              onClick={() => {
                reset()
                setEditing(true)
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--color-white)',
                color: 'var(--color-black)',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              Edit
            </button>
          )}
        </div>
        {applicant ? (
          <>
            <div style={fieldRowStyle}>
              <div>
                <div style={labelStyle}>First Name</div>
                <div style={{ fontSize: '14px', color: 'var(--color-black)' }}>{applicant.firstName || '-'}</div>
              </div>
              <div>
                <div style={labelStyle}>Last Name</div>
                <div style={{ fontSize: '14px', color: 'var(--color-black)' }}>{applicant.lastName || '-'}</div>
              </div>
            </div>
            {/* Feature #221: email + phone sit side-by-side in a 2-col grid */}
            <div style={fieldRowStyle}>
              <div>
                <div style={labelStyle}>Email</div>
                <div style={{ fontSize: '14px', color: 'var(--color-black)' }}>
                  {applicant.email
                    ? <a href={`mailto:${applicant.email}`} style={{ color: 'var(--color-magenta)', textDecoration: 'none' }}>{applicant.email}</a>
                    : '-'}
                </div>
              </div>
              {applicant.phone && (
                <div>
                  <div style={labelStyle}>Phone</div>
                  <div style={{ fontSize: '14px', color: 'var(--color-black)' }}>
                    <a href={`tel:${applicant.phone}`} style={{ color: 'var(--color-magenta)', textDecoration: 'none' }}>{applicant.phone}</a>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ color: 'var(--color-dark-grey)', fontSize: '14px' }}>
            No applicant linked to this application.
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
        Applicant
      </h3>
      <div style={fieldRowStyle}>
        <div>
          <label style={labelStyle}>First Name</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Last Name</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            reset()
            setEditing(false)
          }}
          disabled={saving}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-white)',
            color: 'var(--color-black)',
            border: '1px solid var(--color-light-grey)',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-magenta)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
            fontFamily: 'var(--font-family)',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

export default ApplicantEditor
